/**
 * Product Card Slider
 * Handles the image slider in product cards natively.
 */
class ProductCardSlider extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.product-card__track');
    this.indicatorKnob = this.querySelector('[data-testid="indicator-knob"]');
    this.indicatorWrap = this.querySelector('[data-testid="slide-indicator-wrap"]');
    this.prevBtn = this.querySelector('.product-card__arrow--prev');
    this.nextBtn = this.querySelector('.product-card__arrow--next');
    this.total = parseInt(this.getAttribute('data-total') || '1', 10);
    this.abortController = new AbortController();
    this.userInteracted = false;
  }

  connectedCallback() {
    if (!this.track || this.total <= 1) return;

    this.track.addEventListener('scroll', () => {
      requestAnimationFrame(() => this.updateState());
    }, { passive: true, signal: this.abortController.signal });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.userInteracted = true;
        this.scrollToIndex(this.getCurrentIndex() - 1);
      }, { signal: this.abortController.signal });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.userInteracted = true;
        this.scrollToIndex(this.getCurrentIndex() + 1);
      }, { signal: this.abortController.signal });
    }

    // Hover behavior
    this.addEventListener('pointerenter', (e) => {
      if (e.pointerType === 'mouse' && !this.userInteracted && matchMedia('(hover: hover)').matches) {
        this.scrollToIndex(1, 'auto');
      }
    }, { signal: this.abortController.signal });

    this.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse' && matchMedia('(hover: hover)').matches) {
        const currentIndex = this.getCurrentIndex();
        this.userInteracted = false;
        // Only reset to slide 1 if the user hasn't scrolled past slide 2.
        // If they're on slide 3+ (index >= 2), keep the current position.
        if (currentIndex < 2) {
          this.scrollToIndex(0, 'auto');
        }
      }
    }, { signal: this.abortController.signal });

    // Resize sync
    window.addEventListener('resize', () => {
      this.scrollToIndex(this.getCurrentIndex(), 'auto');
    }, { passive: true, signal: this.abortController.signal });

    this.updateState();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  getCurrentIndex() {
    return Math.round(this.track.scrollLeft / this.track.clientWidth);
  }

  scrollToIndex(index, behavior = null) {
    if (index < 0) index = 0;
    if (index >= this.total) index = this.total - 1;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    behavior = behavior || (prefersReducedMotion ? 'auto' : 'smooth');

    this.track.scrollTo({
      left: index * this.track.clientWidth,
      behavior: behavior
    });
  }

  updateState() {
    const index = this.getCurrentIndex();
    
    if (this.indicatorWrap) {
      this.indicatorWrap.setAttribute('data-current-slide', index + 1);
    }
    if (this.indicatorKnob) {
      this.indicatorKnob.style.transform = `translateX(${index * 100}%)`;
    }

    if (this.prevBtn) {
      this.prevBtn.disabled = index === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = index === this.total - 1;
    }
    
    // Accessibility for slides
    const slides = this.track.querySelectorAll('.product-card__slide');
    slides.forEach((slide, i) => {
      if (i === index) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('inert', 'true');
      }
    });
  }
}

if (!customElements.get('product-card-slider')) {
  customElements.define('product-card-slider', ProductCardSlider);
}
